import { createSelector, lruMemoize } from 'reselect';

import { getDocumentStaleness } from './staleness';
import { selectRedactFiles } from '@common-modules/redactFile';
import type { TDOId } from '@common-modules/universal/models/Brands';
import { namespace, RedactDocumentsState } from './store';
import {
  buildPersistedRedactionState,
  detectionRectKey,
  mergeDetectionAndOverride,
} from './helpers';
import {
  AppliedLexiconRecord,
  AppliedWatermark,
  BatesStampPreview,
  BatesStampSettings,
  WatermarkPreview,
  BoundingBox,
  Detection,
  DetectionSource,
  DocumentsStore,
  hasUnsavedWork,
  KeywordMatch,
  LexiconApplyState,
  LexiconSourcedDetection,
  OcrEngineState,
  OcrManifest,
  OcrPageSource,
  OcrRegion,
  PiiDetection,
  PiiEngineState,
  Redaction,
  SaveState,
  SessionState,
  isLexiconDetection,
  isPiiDetection,
  DocumentBurnProgress,
  RedactAllRun,
} from './models';

/** Stable empty array so the no-session case keeps a referentially-stable value. */
const EMPTY_WATERMARKS: readonly AppliedWatermark[] = [];

/**
 * Selectors for the documents slice. Reselect-memoized where derived state
 * could be hot-path (filtered lists, per-page lookups).
 */

const selectSlice = (state: RedactDocumentsState) => state[namespace];

export const selectSession = (
  state: RedactDocumentsState
): SessionState | null => selectSlice(state).session;

/**
 * VE-29142 — just the tdoId of the document on screen, or null.
 *
 * Narrow on purpose: consumers that only need to know WHICH document is open
 * should not re-render every time anything inside the session changes.
 */
export const selectCurrentTdoId = (state: RedactDocumentsState): TDOId | null =>
  selectSlice(state)?.session?.tdoId ?? null;

export const selectIsEditorOpen = (state: RedactDocumentsState): boolean =>
  selectSlice(state).session !== null;

export const selectLoadError = (state: RedactDocumentsState): string | null =>
  selectSlice(state).loadError;

export const selectIsOrgConfigUnavailable = (
  state: RedactDocumentsState
): boolean => selectSlice(state).orgConfigUnavailable;

export const selectIsExporting = (state: RedactDocumentsState): boolean => {
  // Optional-chain `export` too — older persisted state may not include it,
  // and accessing `.phase` directly on undefined would throw.
  const phase = selectSlice(state).session?.export?.phase;
  return phase === 'preparing' || phase === 'burning' || phase === 'uploading';
};

export const selectCurrentPage = (state: RedactDocumentsState): number =>
  selectSlice(state).session?.currentPage ?? 1;

export const selectZoom = (state: RedactDocumentsState): number =>
  selectSlice(state).session?.zoom ?? 100;

export const selectActiveTool = (state: RedactDocumentsState) =>
  selectSlice(state).session?.activeTool ?? 'select';

export const selectActiveTab = (state: RedactDocumentsState) =>
  selectSlice(state).session?.activeTab ?? 'editor';

/**
 * THE PANEL'S ROWS: the watermark panel's preview while it is open, otherwise
 * the ones the document carries. The list an operator is editing, which is
 * neither what the file will carry nor what the canvas shows.
 *
 * Named for the panel rather than for either of those because it is blind to
 * the on/off switch, so it belongs on neither axis. The switch decides what
 * the page DRAWS (`selectVisibleWatermarks`) and what the export BURNS
 * (`selectOutputMergedWatermarks`), not what there is: turning the marks off
 * has to hide them without emptying the panel out from under the operator,
 * and the sagas that derive artwork or share an image asset read this for the
 * same reason — a mark that is not being drawn is still a mark.
 *
 * It was `selectAppliedWatermarks`, which claimed the one thing it does not
 * mean: an open panel's preview is in here and has not been applied to
 * anything (VE-31894).
 */
export const selectWatermarkRows = (
  state: RedactDocumentsState
): readonly AppliedWatermark[] => {
  const preview = selectSlice(state).watermarkPreview;
  if (preview) {
    return preview.marks;
  }
  return selectSlice(state).session?.watermarks ?? EMPTY_WATERMARKS;
};

/**
 * WHAT THE CANVAS SHOWS — the rows above, minus the panel's switch.
 *
 * Off draws nothing while the panel keeps every row, which is what makes the
 * switch a HIDE rather than a delete; the same split the Bates panel makes
 * between its preview settings and whether they are drawn. With the panel
 * closed the switch is `session.watermarksOff`, and it hides the marks the
 * same way.
 *
 * VISIBLE IS NOT OUTPUT. With the panel open this follows the preview, which
 * nothing has applied; with it closed the two agree. The pair is the point —
 * see `selectOutputMergedWatermarks` for what the burn will carry.
 */
export const selectVisibleWatermarks = (
  state: RedactDocumentsState
): readonly AppliedWatermark[] => {
  const preview = selectSlice(state).watermarkPreview;
  if (preview) {
    return preview.enabled ? preview.marks : EMPTY_WATERMARKS;
  }
  const session = selectSlice(state).session;
  if (!session || session.watermarksOff) {
    return EMPTY_WATERMARKS;
  }
  return session.watermarks;
};

/**
 * The document's OWN hide switch, read on its own.
 *
 * `selectVisibleWatermarks` folds this into its answer, which is right for a
 * document whose marks are all its own. A member of a merged document draws
 * two lists with two switches, so the composed selector needs this one
 * separately rather than through a collapsed result.
 */
export const selectWatermarksOff = (state: RedactDocumentsState): boolean =>
  selectSlice(state).session?.watermarksOff ?? false;

/** The marks the document itself carries — what Apply would be changing. */
export const selectCommittedWatermarks = (
  state: RedactDocumentsState
): readonly AppliedWatermark[] =>
  selectSlice(state).session?.watermarks ?? EMPTY_WATERMARKS;

/** The watermark panel's unapplied preview, or null when it is closed. */
export const selectWatermarkPreview = (
  state: RedactDocumentsState
): WatermarkPreview | null => selectSlice(state).watermarkPreview ?? null;

/** The document's Bates stamping (VE-24447), or null when none is applied. */
export const selectBatesStamp = (
  state: RedactDocumentsState
): BatesStampSettings | null => selectSlice(state).session?.batesStamp ?? null;

/** The Bates panel's unapplied preview, or null when the panel is closed. */
export const selectBatesPreview = (
  state: RedactDocumentsState
): BatesStampPreview | null => selectSlice(state).batesPreview ?? null;

/** Whether the watermark panel is open — also gates on-canvas drag/resize. */
/**
 * VE-24947 — the document a switch is heading to, or null when none is in
 * flight. While this is set the editor keeps showing the outgoing document's
 * chrome; the panes that belong to a specific document show a loading state.
 */
export const selectPendingDocument = (
  state: RedactDocumentsState
): DocumentsStore['pendingDocument'] =>
  selectSlice(state)?.pendingDocument ?? null;

/**
 * VE-24947 (U2-FR2) — every open document that has unsaved work: the active one
 * when it is dirty, plus every suspended one that still is.
 *
 * Suspended USED to imply dirty — `suspendIfWorthKeeping` parks nothing else — and
 * this read every suspended session unconditionally on that basis. Save All
 * ended it: it persists suspended documents in place, so a parked session can
 * now go clean without ever coming back on screen. Reading the map wholesale
 * after that reported saved work as unsaved, which is the leave prompt
 * offering to discard something already on the server. Filter, do not assume.
 *
 * This is the count the close prompt needs ("N documents have unsaved
 * changes"), and the set a Save All acts on. Read this rather than
 * `suspendedSessions` directly: the document on screen is the one a reviewer is
 * most likely to have just edited, and it does not live in that map.
 */
/**
 * Every open document the editor KNOWS HAS BEEN WORKED ON — unsaved edits, a
 * save in flight, or a save that has already happened.
 *
 * NOT `selectUnsavedDocuments`, and the difference is one status.
 * `hasUnsavedWork` is `dirty || error` and excludes `saving` deliberately: it
 * answers "should we warn the reviewer before they lose this", and an
 * autosave landing as somebody closes the editor must not accuse them of
 * unsaved work.
 *
 * "HAS ANYBODY WORKED ON THIS FILE" is a different question and `saving` is
 * where the two answers diverge. Pressing Save takes a file out of the
 * unsaved set the instant the request leaves, while the record proving the
 * save only arrives with the response — so for the length of the round trip
 * the file has no unsaved work, no saved redactions and no output, and every
 * reader concludes nobody has ever touched it. The row flashed the grey
 * never-worked-on minus between the unsaved badge and the saved-never-burned
 * ring; the merged document's Update offered the file to
 * `IncludeUnworkedDialog` and, unticked, left it out of the release.
 *
 * AND `lastSavedAt`, WHICH CLOSES THE OTHER END. Covering `saving` alone
 * fixed the window before the response and left the one after it: the save
 * record reaches the readers by being PATCHED ONTO A ROW
 * (`redactionDataSaved`), and on a document the per-row query has not reached
 * there is no row to patch — the patch is dropped, and a row fetch landing
 * afterwards appends the server's pre-save snapshot over the top. The file
 * then has no unsaved work, no save record and no output, and the grey
 * never-worked-on minus appears for as long as that takes to correct.
 *
 * This answers from the session instead, which is the better source for any
 * OPEN document: the editor loaded the redaction asset itself. `lastSavedAt`
 * is set by SAVE_SUCCESS and seeded on open ONLY from a redaction asset that
 * is not machine-written (`openDocument`) — the same `isMachineWrittenRedactionAsset`
 * line the rows are judged by, so the upload pipeline's lexicon stub is
 * excluded here exactly as it is there. Non-null means a person saved this
 * document, and no row has to have heard about it.
 *
 * A SELECTOR AND NOT A COMPONENT MEMO, because the question has more than one
 * reader: the Documents panel's row marks and work list, and `useMergeUpdate`
 * on the sibling press. It was derived in the panel first and the two drifted
 * immediately — the panel stopped flashing and the Update press did not.
 * Anything passing `unsavedTdoIds` to `neverWorkedOnMembers` or
 * `buildRedactAllWorkList` wants THIS.
 *
 * OPEN SESSIONS ONLY, which bounds it: a file nobody has opened this visit is
 * judged by its row as before.
 *
 * KEYED THROUGH A STRING so the Set is referentially stable. Built straight
 * off the sessions it would be a new object on every edit — a box drag, a
 * page change — and every consumer rebuilds a work list over every document
 * in the request. The ids only change when a save starts or finishes.
 */
const selectEditorWorkTdoIdsKey = createSelector(
  (state: RedactDocumentsState) => selectSlice(state)?.session,
  (state: RedactDocumentsState) => selectSlice(state)?.suspendedSessions,
  (session, suspended): string => {
    const holdsWork = (save: SaveState) =>
      hasUnsavedWork(save) ||
      save.status === 'saving' ||
      save.lastSavedAt !== null;
    const ids = Object.values(suspended ?? {})
      .filter((s) => holdsWork(s.save))
      .map((s) => String(s.tdoId));
    if (session && holdsWork(session.save)) {
      ids.push(String(session.tdoId));
    }
    return ids.sort().join(',');
  }
);

export const selectEditorWorkTdoIds = createSelector(
  selectEditorWorkTdoIdsKey,
  (key): ReadonlySet<string> => new Set(key ? key.split(',') : [])
);

export const selectUnsavedDocuments = createSelector(
  // Tolerant of a missing slice, unlike the session selectors above: this one
  // is read at PAGE level (the route's leave guard), which can render before or
  // outside a registered documents slice.
  (state: RedactDocumentsState) => selectSlice(state)?.session,
  (state: RedactDocumentsState) => selectSlice(state)?.suspendedSessions,
  (session, suspended): { tdoId: TDOId; documentName: string }[] => {
    const rows = Object.values(suspended ?? {})
      .filter((s) => hasUnsavedWork(s.save))
      .map((s) => ({
        tdoId: s.tdoId,
        documentName: s.documentName,
      }));
    if (session && hasUnsavedWork(session.save)) {
      rows.unshift({
        tdoId: session.tdoId,
        documentName: session.documentName,
      });
    }
    return rows;
  }
);

/**
 * VE-24947 — TDOs the editor is holding open: the active document plus every
 * suspended one.
 *
 * This is the set that must hold a TDO lock. A suspended document still carries
 * the reviewer's unsaved work, so releasing its lock would let a colleague edit
 * underneath work that is going to be saved over the top of theirs.
 */
export const selectOpenDocumentIds = createSelector(
  (state: RedactDocumentsState) => selectSlice(state)?.session,
  (state: RedactDocumentsState) => selectSlice(state)?.suspendedSessions,
  (session, suspended): TDOId[] => {
    const ids = Object.keys(suspended ?? {}) as TDOId[];
    return session && !ids.includes(session.tdoId)
      ? [session.tdoId, ...ids]
      : ids;
  }
);

/**
 * VE-24947 — is the editor's Documents panel open? Slice-level, so it survives
 * the document switch it is used to perform.
 */
export const selectDocumentsPanelOpen = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelOpen ?? false;

/**
 * VE-29142 — should the Documents panel arrive at full width? See
 * `DocumentsStore.documentsPanelExpanded`; a restore says no, an open says yes.
 */
export const selectDocumentsPanelExpanded = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelExpanded ?? false;

/**
 * VE-24947 (U2-FR5) — the reviewer's document order for `folderId`, or null if
 * they have not set one, or set one for a different request.
 */
export const selectDocumentOrderFor =
  (folderId: string | undefined) =>
  (state: RedactDocumentsState): TDOId[] | null => {
    const stored = selectSlice(state)?.documentOrder;
    return stored && folderId && stored.folderId === folderId
      ? stored.order
      : null;
  };

/**
 * VE-24947 — every session suspended by a switch, keyed by TDO. For callers
 * that must act on ALL open documents rather than the one on screen (save all).
 */
export const selectSuspendedSessions = (
  state: RedactDocumentsState
): Record<string, SessionState> => selectSlice(state)?.suspendedSessions ?? {};

/**
 * What the editor's burn strip is saying about the open document, or null.
 *
 * On the SESSION rather than the slice, so `markDirty` can take it away with
 * the first edit and so a suspended document keeps its own.
 */
export const selectExportNotice = (
  state: RedactDocumentsState
): 'running' | 'complete' | null =>
  // `export?`, matching `markDirty`'s own guard: it is declared non-optional
  // on the type, so tsc cannot say which of the two readings is right, and a
  // hand-built session in a test has been known to arrive without it.
  selectSlice(state)?.session?.export?.notice ?? null;

/**
 * Did anything change the open document during the burn being tracked?
 *
 * `undefined` MEANS NO BURN IS TRACKED and is deliberately not collapsed into
 * `false`: a Redact All pass burns without an EXPORT_REQUEST, so "nobody
 * interrupted" and "nobody was watching" are different answers and only one
 * of them is a reason to tell the reviewer their output is behind.
 */
export const selectExportInterrupted = (
  state: RedactDocumentsState
): boolean | undefined => selectSlice(state)?.session?.export?.interrupted;

/** VE-24947 — is an explicit Save All pass running? See the model's note. */
export const selectSaveAllInProgress = (state: RedactDocumentsState): boolean =>
  selectSlice(state)?.saveAllInProgress ?? false;

/**
 * is the Documents panel's "Not included" section folded away? Slice-level so
 * a document switch made from the panel does not unfold it again.
 */
export const selectDocumentsPanelNotIncludedCollapsed = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelNotIncludedCollapsed ?? false;

/**
 * is the Documents panel's "Merged files" section folded away? Its twin.
 */
export const selectDocumentsPanelMergedCollapsed = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelMergedCollapsed ?? false;

/**
 * The membership the panel's list is arranged by while Add / Remove is open,
 * with the project it came from. See the action's note.
 */
export const selectDocumentsPanelMembershipSnapshot = (
  state: RedactDocumentsState
): {
  projectTdoId: string;
  members: readonly string[];
} | null => selectSlice(state)?.documentsPanelMembershipSnapshot ?? null;

/**
 * is the Documents panel in Add / Remove? See the action's note.
 */
export const selectDocumentsPanelEditingMembership = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelEditingMembership ?? false;

/** VE-24947 — is the Documents panel pinned open? */
export const selectDocumentsPanelPinned = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.documentsPanelPinned ?? false;

/** Does the tool rail show its buttons' names? Remembered per document. */
export const selectToolPanelExpanded = (state: RedactDocumentsState): boolean =>
  selectSlice(state)?.toolPanelExpanded ?? false;

/** VE-24947 — is the Pages panel hidden? Survives a document switch. */
export const selectPagesPanelCollapsed = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.pagesPanelCollapsed ?? false;

/** The unworked set already declined — see `declinedUnworkedKey`. */
export const selectDeclinedUnworkedKey = (
  state: RedactDocumentsState
): string | null => selectSlice(state)?.declinedUnworkedKey ?? null;

export const selectWatermarkEditing = (state: RedactDocumentsState): boolean =>
  selectSlice(state).session?.watermarkEditing ?? false;

/** The selected watermark's id — shared by the panel list and the canvas handles. */
export const selectSelectedWatermarkId = (
  state: RedactDocumentsState
): string | null => selectSlice(state).session?.selectedWatermarkId ?? null;

/**
 * ONE frozen fallback, not a fresh literal per call.
 *
 * This selector is read with `useSelector`, which compares by reference: a new
 * object every call means every render sees a changed value, which schedules
 * another render, which builds another object. With no session open — the
 * state the editor route sits in while the document loads — an inline literal
 * here is an infinite render loop in any component that reads it before
 * `session` exists ("Maximum update depth exceeded"). It went unnoticed for as
 * long as the only consumer was DocumentEditor, which mounts only once a
 * session is open and so never reached the fallback.
 */
const CLEAN_SAVE_STATE: SaveState = Object.freeze({
  status: 'clean',
  lastSavedAt: null,
  rev: 0,
});

export const selectSaveStatus = (state: RedactDocumentsState): SaveState =>
  selectSlice(state).session?.save ?? CLEAN_SAVE_STATE;

/**
 * Undo/redo, and whether the rail's buttons are live.
 *
 * Both go dead while either mark panel is previewing, matching the reducer,
 * which refuses the actions outright: an undo cannot be routed into a preview
 * (it restores the whole session in one go), and one the operator could not see
 * happen would be worse than one they could not make. The preview's own undo is
 * Cancel. Disabling the buttons is what makes that visible rather than making
 * them look broken.
 */
const isPreviewing = (state: RedactDocumentsState): boolean =>
  !!selectSlice(state).watermarkPreview || !!selectSlice(state).batesPreview;

export const selectCanUndo = (state: RedactDocumentsState): boolean =>
  !isPreviewing(state) &&
  (selectSlice(state).session?.undoStack.past.length ?? 0) > 0;

export const selectCanRedo = (state: RedactDocumentsState): boolean =>
  !isPreviewing(state) &&
  (selectSlice(state).session?.undoStack.future.length ?? 0) > 0;

export const selectVersionHistory = (state: RedactDocumentsState) =>
  selectSlice(state).session?.versionHistory ?? [];

// ---------------------------------------------------------------------------
// "Send to" integrations (VE-23442)
// ---------------------------------------------------------------------------

const NO_VENDOR_REQUEST_IDS = {
  govQa: null,
  foiaXpress: null,
  casepoint: null,
  exterro: null,
  nuix: null,
} as const;

/** Vendor request-ids read from the TDO at open; drives button visibility. */
export const selectVendorRequestIds = (state: RedactDocumentsState) =>
  selectSlice(state).session?.vendorRequestIds ?? NO_VENDOR_REQUEST_IDS;

/** The full "Send to" integration state slice (per-integration send/dialog). */
export const selectDocumentIntegrations = (state: RedactDocumentsState) =>
  selectSlice(state).integrations;

export const selectRedactionStatus = (
  state: RedactDocumentsState
): string | null => selectSlice(state).session?.redactionStatus ?? null;

/** Frozen human-readable status name for orphan display (VE-24546). */
export const selectRedactionStatusName = (
  state: RedactDocumentsState
): string | null => selectSlice(state).session?.redactionStatusName ?? null;

export const selectPiiEngineState = (
  state: RedactDocumentsState
): PiiEngineState => selectSlice(state).session?.piiEngineState ?? 'not-run';

// ---------------------------------------------------------------------------
// Redact Document OCR
// ---------------------------------------------------------------------------

/**
 * Deliberately NOT exported. Surfaces read `selectOcrSearchOutlook` instead —
 * two of them derived their own subset of this state and drifted apart, which
 * is the bug that made the editor claim a document had no searchable text while
 * OCR was still reading it. Keeping the raw state module-private is what stops
 * that recurring.
 */
const selectOcrEngineState = (state: RedactDocumentsState): OcrEngineState =>
  selectSlice(state).session?.ocrEngineState ?? 'not-run';

/**
 * What the OCR pipeline implies for SEARCH, as the four cases the UI copy
 * actually has to distinguish.
 *
 * `ocrEngineState` has seven values because it also tracks how it was resolved;
 * the surfaces that talk to the reviewer care about less than that, and each of
 * them was deriving its own subset. They drifted — the rail treated `checking`
 * as settled and asserted "no pages have searchable text" before the check that
 * would have contradicted it had come back, and the page notice reverted to the
 * same claim the moment OCR finished and a reload was pending. One derivation,
 * so the two cannot disagree again:
 *
 *   - `unknown`        — still resolving, or unresolvable. Assert NOTHING:
 *     "no searchable text" is a claim about a document we have not finished
 *     looking at, and a lookup that failed is not evidence that OCR never ran.
 *   - `running`        — OCR is reading the pages now; search is about to widen.
 *   - `reload-pending` — OCR finished and republished. The file on screen is
 *     still the one this session pinned, so its pages really are unsearchable —
 *     but that is one reload away from being false.
 *   - `failed`         — the engine ran and ended badly, and `timed-out` —
 *     it never finished. Both used to collapse into `settled`, so a broken run
 *     reported "no pages have searchable text — manual review recommended",
 *     which blames the DOCUMENT for the engine's failure and offers the
 *     reviewer nothing to do about it.
 *   - `settled`        — nothing is coming, and nothing went wrong. This is the
 *     only case where the un-hedged "no searchable text" is honest.
 */
export type OcrSearchOutlook =
  | 'unknown'
  | 'running'
  | 'reload-pending'
  /** The run ended badly. NOT `settled` — see below. */
  | 'failed'
  /** Still unfinished a day on; nothing is coming without a new run. */
  | 'timed-out'
  | 'settled';

export const ocrSearchOutlook = (
  ocrEngineState: OcrEngineState
): OcrSearchOutlook => {
  switch (ocrEngineState) {
    case 'checking':
    case 'indeterminate':
      return 'unknown';
    case 'processing':
      return 'running';
    case 'refresh-available':
      return 'reload-pending';
    case 'failed':
      return 'failed';
    case 'timed-out':
      return 'timed-out';
    default:
      return 'settled';
  }
};

export const selectOcrSearchOutlook = (
  state: RedactDocumentsState
): OcrSearchOutlook => ocrSearchOutlook(selectOcrEngineState(state));

/**
 * Whether a NEW OCR run is a thing this document can be offered.
 *
 * TWO CASES, and they are not the same offer: the engine has never run here
 * (`not-run`), or it ran and ended badly (`failed` / `timed-out`) and running
 * it again is the remedy. Everything else — a run in flight, one finished
 * cleanly, a republished file waiting on a reload — already has its own answer
 * on screen, and offering "Run OCR Detection" over any of them invites a second
 * job that duplicates the first.
 *
 * Reads the raw engine state, which is why it lives here rather than at a call
 * site: `ocrSearchOutlook` folds `not-run` and `done` together into `settled`
 * (it answers a question about SEARCH, where the two are identical), so the
 * distinction this needs is only available inside this module.
 *
 * DOES NOT include whether an OCR engine is configured for the org — that comes
 * from the engines module (`selectConfigEngines().documentOcrEngineId`) and the
 * caller combines the two. Nor anything per-page: this is a document-wide run.
 *
 * Shared by the canvas page notice and the Pages rail so a reviewer is not
 * offered the run in one place and refused it in the other.
 */
export const selectCanStartOcr = (state: RedactDocumentsState): boolean => {
  const engineState = selectOcrEngineState(state);
  const outlook = ocrSearchOutlook(engineState);
  return (
    outlook === 'failed' || outlook === 'timed-out' || engineState === 'not-run'
  );
};

export const selectOcrManifest = (
  state: RedactDocumentsState
): OcrManifest | null => selectSlice(state).session?.ocrManifest ?? null;

/** Whether the OCR-flagged region overlay is switched on. */
export const selectShowOcrRegions = (state: RedactDocumentsState): boolean =>
  selectSlice(state).session?.showOcrRegions ?? false;

/**
 * Whether any page of the open document is KNOWN to carry no text layer.
 *
 * "Known" is the whole content of this. `pageSearchability` fills in as the
 * pre-pass and the on-view probe reach each page, so a page that is absent, or
 * still `unknown`, has not been judged yet — and `blank` is a page that was
 * judged and found genuinely empty, which OCR has nothing to add to. Only
 * `unsearchable` means "there is content here and no text layer over it".
 *
 * A POSITIVE signal, which is why a partial map is safe to act on: one page
 * already found unsearchable is enough to know OCR has work to do, whatever the
 * pages nobody has looked at yet turn out to be. Contrast
 * `allPagesUnsearchable` in `DocumentEditorView`, which is a claim about EVERY
 * page and therefore refuses to answer until the map covers the document.
 */
export const selectHasUnsearchablePages = (
  state: RedactDocumentsState
): boolean =>
  Object.values(selectSlice(state).session?.pageSearchability ?? {}).some(
    (searchability) => searchability === 'unsearchable'
  );

/**
 * Whether the scan that decides which pages need OCR has finished.
 *
 * TWO PASSES, and only the second one answers the question. The text pre-pass
 * indexes every page and leaves the text-less ones provisionally `blank`,
 * because decoding a page just to classify it blocked scanned documents for
 * tens of seconds (VE-27250); the raster probe forked after it is what promotes
 * the ones that actually paint content to `unsearchable`. So `textIndexReady`
 * alone is not enough — mid-scan it is true while every scanned page still
 * reads as blank.
 */
export const selectOcrNeedScanComplete = (
  state: RedactDocumentsState
): boolean => {
  const session = selectSlice(state).session;
  return !!session?.textIndexReady && !!session?.searchabilityScanReady;
};

/** Probe progress for the row that explains the wait; null before it starts. */
export const selectSearchabilityScanProgress = (
  state: RedactDocumentsState
): { scanned: number; total: number } | null =>
  selectSlice(state).session?.searchabilityScanProgress ?? null;

/**
 * What the detections panel may let the reviewer start, while OCR is running.
 *
 *   - `open`             — go ahead. Either OCR has nothing to do with this
 *                          document, or it is involved and this document is
 *                          known to need none of it.
 *   - `scanning`         — we do not know yet. The pass that separates a
 *                          scanned page from a blank one has not finished.
 *   - `blocked`          — we know: pages here need OCR, and it is still
 *                          working.
 *   - `awaiting-reload`  — OCR has FINISHED, and the session is still rendering
 *                          the file it opened. Its pages are as textless as
 *                          they ever were; the searchable copy is one reload
 *                          away and nothing here can read it until then.
 *
 * WHY DETECTION WAITS AT ALL. PII detection reads text. Run against a document
 * whose pages are still images it finds nothing and reports a CLEAN DOCUMENT,
 * which is the worst answer the panel can give — a reviewer told there is no
 * PII stops looking. `runPii.ts` chains OCR ahead of detection to prevent
 * exactly this, but only from `ocrEngineState === 'not-run'`, so a run already
 * in flight fell straight through to a PII-only job against the pre-OCR file.
 * Lexicons wait for the same reason and one more: a lexicon applied before the
 * text layer exists matches nothing, and re-applying every lexicon after OCR
 * lands is work nobody should have to remember to do.
 *
 * WHY `scanning` IS ITS OWN ANSWER rather than folding into `open`. The scan
 * takes real time on a long document — the whole text pre-pass, and then the
 * probe over every text-less page — and for most of it nothing is marked
 * `unsearchable` yet. Treating "nothing marked" as "nothing needs OCR" left the
 * control live over exactly the documents it protects, for exactly as long as
 * the answer was unknown. Unknown is now a wait, not a yes.
 *
 * WHY `reload-pending` IS ALSO A REFUSAL, and not the same one. The engine is
 * done, so "wait for OCR" is false — but the document on screen is the pre-OCR
 * file and matching it would find exactly what it would have found an hour ago.
 * A run started here is the same silent under-redaction, reached by waiting
 * rather than by rushing, so the control refuses and the reason names the
 * reload instead of the engine.
 *
 * WHERE IT DOES END: `failed` and `timed-out`. Nothing is coming for those, so
 * refusing would be refusing for ever, and a reviewer who runs detection over a
 * document OCR could not read is making an informed choice the panel has
 * already told them about.
 *
 * Reads the outlook rather than `ocrEngineState`, like every other surface —
 * see `selectOcrSearchOutlook` for why that is not optional.
 */
export type PiiRunGate = 'open' | 'scanning' | 'blocked' | 'awaiting-reload';

export const selectPiiRunGate = (state: RedactDocumentsState): PiiRunGate => {
  /**
   * NOT KNOWN YET IS NOT A YES.
   *
   * A fresh session starts at `checking` and stays there until the engine
   * poller's first read comes back. The gate used to open for that window,
   * because `checking` is not `running` — so on a document OCR was in fact
   * working on, both controls were live until the poller answered. One round
   * trip, not a poll interval, but it is the same wrong answer the rest of this
   * exists to prevent, and it lands at the exact moment a reviewer is most
   * likely to reach for the button.
   *
   * ONLY `checking`, deliberately, and this is why it reads the raw state that
   * the rest of the file is forbidden from touching: `ocrSearchOutlook`
   * collapses `checking` and `indeterminate` into one `unknown`, which is right
   * for describing SEARCH — neither licenses a claim — and wrong here.
   * `checking` is transient and about to resolve; `indeterminate` is settled
   * and never will. Refusing on the second would refuse for ever.
   *
   * Costs one status round trip on every document open, which is why the
   * controls flicker disabled and back on a document that needed none of this.
   * The alternative is being confidently wrong for the same window.
   */
  if (selectOcrEngineState(state) === 'checking') {
    return 'scanning';
  }
  const outlook = selectOcrSearchOutlook(state);
  if (outlook !== 'running' && outlook !== 'reload-pending') {
    return 'open';
  }
  if (!selectOcrNeedScanComplete(state)) {
    return 'scanning';
  }
  // The scan has finished and had its say. No pages needing OCR means this is a
  // born-digital document OCR is passing over, and everything can read it right
  // now whichever state the engine is in.
  if (!selectHasUnsearchablePages(state)) {
    return 'open';
  }
  return outlook === 'running' ? 'blocked' : 'awaiting-reload';
};

/**
 * Whether a lexicon run in flight is UPDATING results rather than producing
 * them for the first time.
 *
 * The distinction is the whole message: a first apply is the reviewer's own
 * action and needs no explaining, where a re-run they did not ask for — the one
 * the reload triggers once `evaluatedAssetId` goes stale — arrives unannounced
 * and changes counts they may already have read.
 *
 * Read off the records rather than the run: during the run they still carry the
 * PREVIOUS evaluation, so "something here was already evaluated" is exactly
 * "this is not the first time".
 *
 * TRUE FROM THE MOMENT THE RELOAD IS ASKED FOR, not from when the re-run
 * starts. The run cannot begin until the new session's pre-pass has extracted
 * text, which on a long document is seconds later — and the panel's numbers are
 * stale for all of it, so a notice keyed on the run alone arrived after the
 * window it was describing. `ocrReloadInProgress` covers the gap and the run
 * covers the rest; either is enough.
 */
export const selectLexiconMatchesUpdating = (
  state: RedactDocumentsState
): boolean => {
  if (selectSlice(state).ocrReloadInProgress) {
    return true;
  }
  const session = selectSlice(state).session;
  if (session?.lexiconApply.status !== 'running') {
    return false;
  }
  return session.appliedLexicons.some((record) => !!record.evaluatedAt);
};

/**
 * Where each page's text came from, keyed by 1-based page number.
 *
 * Empty when there is no manifest — which callers must read as "nothing known",
 * NOT as "no page was OCR'd". A page absent from the map gets no OCR badge and
 * no OCR-specific warning; it falls back to the pdfjs searchability verdict.
 */
export const selectOcrPageSources = createSelector(
  [selectOcrManifest],
  (manifest): Record<number, OcrPageSource> => {
    if (!manifest) {
      return {};
    }
    const byPage: Record<number, OcrPageSource> = {};
    for (const record of manifest.pages) {
      byPage[record.page] = record.source;
    }
    return byPage;
  }
);

/**
 * Pages OCR tried to read and could not (`source: 'failed'`).
 *
 * These are the pages that must never present as searched: they have no text,
 * so pdfjs would classify them exactly like a page nobody ever tried to read,
 * and the reviewer would get the generic "no searchable text" notice instead of
 * being told the attempt failed.
 */
export const selectOcrFailedPages = createSelector(
  [selectOcrManifest],
  (manifest): number[] =>
    (manifest?.pages ?? [])
      .filter((p) => p.source === 'failed')
      .map((p) => p.page)
);

/**
 * Regions OCR flagged, keyed by 1-based page number.
 *
 * Derived once per manifest so each page's array is referentially stable — the
 * canvas passes one page's slice into a memoized `DocumentPage`, and rebuilding
 * these per page would be quadratic on a long document.
 *
 * Boxes are already PDF points with a top-left origin, the frame the overlay
 * draws in, so no conversion happens anywhere downstream.
 */
export const selectOcrRegionsByPage = createSelector(
  [selectOcrManifest],
  (manifest): Record<number, OcrRegion[]> => {
    if (!manifest) {
      return {};
    }
    const byPage: Record<number, OcrRegion[]> = {};
    for (const record of manifest.pages) {
      if (record.regions?.length) {
        byPage[record.page] = record.regions;
      }
    }
    return byPage;
  }
);

/**
 * Pages where OCR found writing but wrote no text for it (`unread` regions) —
 * it could not read the writing, or read it below the confidence threshold and
 * withheld it.
 *
 * These are searchable pages with a gap: the page carries a text layer, so it
 * presents as searched, but keyword search cannot reach those areas. A Record
 * for O(1) lookup — the rail tests it per thumbnail (VE-27794).
 */
export const selectOcrUnreadPages = createSelector(
  [selectOcrRegionsByPage],
  (byPage): Record<number, true> => {
    const pages: Record<number, true> = {};
    for (const [page, regions] of Object.entries(byPage)) {
      if (regions.some((r) => r.kind === 'unread')) {
        pages[Number(page)] = true;
      }
    }
    return pages;
  }
);

/**
 * How many pages OCR read, for the document-level best-effort notice.
 *
 * "Read" as the manifest uses it — OCR looked at the page — so `empty` counts:
 * finding no text is evidence the page is blank, not proof, and the notice's
 * claim ("may contain information the tool did not find") holds there. Also
 * keeps the count matching the OCR badges, which mark every page OCR ran on.
 *
 * Excludes `failed`, which the per-page warning already reports as unsearched.
 */
export const selectOcrReadPageCount = createSelector(
  [selectOcrManifest],
  (manifest): number =>
    (manifest?.pages ?? []).filter(
      (p) => p.source === 'ocr' || p.source === 'empty'
    ).length
);

// ---------------------------------------------------------------------------
// Detections
// ---------------------------------------------------------------------------

/**
 * Every detection card in the session — PII and lexicon alike (VE-24948 / U5).
 *
 * `selectPiiDetections` is the narrowed view for consumers that genuinely only
 * handle engine output.
 */
export const selectDetections = (state: RedactDocumentsState): Detection[] =>
  selectSlice(state).session?.detections ?? [];

/** PII-engine rows only. */
export const selectPiiDetections = createSelector(
  [selectDetections],
  (detections): PiiDetection[] => detections.filter(isPiiDetection)
);

/** Lexicon-sourced rows only (`U5-FR15`). */
export const selectLexiconDetections = createSelector(
  [selectDetections],
  (detections): LexiconSourcedDetection[] =>
    detections.filter(isLexiconDetection)
);

/** Lexicons applied to the open document, in application order. */
export const selectAppliedLexicons = (
  state: RedactDocumentsState
): readonly AppliedLexiconRecord[] =>
  selectSlice(state).session?.appliedLexicons ?? [];

/** Lexicon ids the reviewer has hidden in the Categories dropdown (M5). */
export const selectLexiconFilterUnchecked = (
  state: RedactDocumentsState
): readonly string[] =>
  selectSlice(state).session?.lexiconFilterUnchecked ?? [];

/** Progress/outcome of lexicon application in this session. */
export const selectLexiconApply = (
  state: RedactDocumentsState
): LexiconApplyState | null => selectSlice(state).session?.lexiconApply ?? null;

/** Lexicons picked in the upload dialog, awaiting the next upload (`U5-FR14`). */
export const selectUploadLexiconSelection = (
  state: RedactDocumentsState
): readonly string[] => selectSlice(state).uploadLexiconSelection;

/**
 * Which selected lexicons run with auto-redact (VE-24948 / U5).
 *
 * `?? EMPTY` because this key arrived after the slice did: a store built
 * before it existed — a test fixture, a rehydrated state — has no value for
 * it, and a component mapping over `undefined` is a blank upload dialog
 * rather than a missing toggle. The constant, not a literal, so the identity
 * is stable and the selector cannot drive a re-render on every call.
 */
const NO_AUTO_REDACT: readonly string[] = Object.freeze([]);
export const selectUploadLexiconAutoRedact = (
  state: RedactDocumentsState
): readonly string[] =>
  selectSlice(state).uploadLexiconAutoRedact ?? NO_AUTO_REDACT;

/** The selection staged against the in-flight upload batch (`U5-FR14`). */
export const selectUploadLexiconBatch = (
  state: RedactDocumentsState
): DocumentsStore['uploadLexiconBatch'] =>
  selectSlice(state).uploadLexiconBatch;

/**
 * The detection list narrowed by the SEARCH BOX only — what `DetectionsPanel`
 * is handed (VE-29286).
 *
 * Deliberately NOT narrowed by the PII category checkboxes. The panel applies
 * those itself (`visibleDetections`), and it needs the hidden rows to still be
 * here: the Categories dropdown lists a type because the results contain it
 * (`piiTypeCounts`), Select all / Clear reads which listed types are ticked,
 * the empty state asks whether a filter is hiding anything, and the source
 * strip asks whether PII rows exist at all. Feeding it the category-filtered
 * list made a hidden type vanish from the dropdown with no way to tick it
 * again, and Clear emptied the dropdown outright.
 *
 * The needle applies to BOTH populations. It used to skip lexicon rows, which
 * sort to the top of the list — so typing a term with no PII hits left every
 * lexicon card on screen, burying the PII matches the reviewer was searching
 * for (review-gate M4). PII rows match on label and type; lexicon rows on
 * label and the lexicon's name as applied — the things each row shows.
 */
export const selectSearchedDetections = createSelector(
  [
    selectPiiDetections,
    selectLexiconDetections,
    (state: RedactDocumentsState) =>
      selectSlice(state).session?.detectionSearch ?? '',
  ],
  (pii, lexicon, search): Detection[] => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return [...pii, ...lexicon];
    }
    return [
      ...pii.filter(
        (d) =>
          d.label.toLowerCase().includes(needle) ||
          d.piiType.toLowerCase().includes(needle)
      ),
      ...lexicon.filter(
        (d) =>
          d.label.toLowerCase().includes(needle) ||
          d.lexicon.lexiconName.toLowerCase().includes(needle)
      ),
    ];
  }
);

/**
 * Is the Detections tab the one on screen? The same two-part test the canvas
 * uses for candidate visibility (`piiTabActive`, lasso, marquee): in the flat
 * layout `redactionsSubtab === 'pii'` means the Detections tab, but switching
 * to Comments changes `detectionsTab` and leaves the subtab alone — so the
 * subtab alone read "Detections" while Comments was showing (VE-30011).
 */
export const selectDetectionsTabShowing = (
  state: RedactDocumentsState
): boolean => {
  const session = selectSlice(state).session;
  return (
    session?.detectionsTab === 'detections' &&
    session.redactionsSubtab === 'pii'
  );
};

/**
 * Does the document hold BOTH kinds of detection — the condition for the
 * Detections tab's `All | PII | Lexicon` strip (VE-30011)? Read from the store,
 * not the searched list, so a search that empties one kind does not switch the
 * source off. Memoised on the two per-kind lists, so it is not a scan per
 * dispatch.
 */
export const selectHasBothDetectionSources = createSelector(
  [selectPiiDetections, selectLexiconDetections],
  (pii, lexicon): boolean => pii.length > 0 && lexicon.length > 0
);

/**
 * The searched list further narrowed by the Detections tab's filters — what the
 * editor CANVAS draws (`DocumentEditorView`'s `canvasSession`, and through it
 * DocumentPage, DetectionMaskOverlay and the lasso/marquee hit-tests).
 *
 * Two populations, filtered differently:
 *
 *   - PII rows pass through the redux PII-type filter (and the search needle,
 *     via `selectSearchedDetections`).
 *   - Lexicon rows pass through the search needle and the lexicon filter
 *     (`lexiconFilterUnchecked`, by `lexicon.lexiconId`). They have no
 *     `piiType` to satisfy the type filter.
 *
 * Then the source strip (`detectionSource`, VE-30011), only when both kinds
 * are present — which is when the strip itself renders.
 *
 * ALL THREE — category, lexicon, source — apply only while the Detections tab
 * is showing (`selectDetectionsTabShowing`). They are ways of LOOKING at candidates. Applied everywhere,
 * they hid COMMITTED boxes on the Redactions tab — boxes Redact File still
 * burns in — so the canvas stopped showing what the export would contain
 * (review-gate D1). The category filter used to do that as an undesigned side
 * effect of `canvasSession`; unchecking a lexicon, by contrast, used to hide
 * nothing at all (VE-30011). The search needle is gated the same way: its
 * field renders only on the Detections tab and nothing clears it on a tab
 * switch, so applied everywhere it hid committed boxes behind a filter that was
 * not on screen (review-gate R2-N2). Off the Detections tab every row passes.
 *
 * The second population is the load-bearing line. This selector used to return
 * PII rows only, with a note that lexicon rows would arrive "once Step 7/8 of
 * VE-24948 lands the panel work". The panel work landed and the feed was never
 * re-plumbed — so every lexicon card the store held was filtered out here,
 * before the panel could see it. The store said "15 matches", the list said
 * "0 in this list", and both were telling the truth about different arrays.
 *
 * NOT for the detections list — see `selectSearchedDetections` for why the
 * panel must see the rows this one takes out.
 */
export const selectFilteredDetections = createSelector(
  [
    selectSearchedDetections,
    selectPiiDetections,
    selectLexiconDetections,
    selectDetectionsTabShowing,
    (state: RedactDocumentsState) =>
      selectSlice(state).session?.detectionFilter,
    (state: RedactDocumentsState) =>
      selectSlice(state).session?.lexiconFilterUnchecked,
    (state: RedactDocumentsState) =>
      selectSlice(state).session?.detectionSource ?? 'all',
    selectHasBothDetectionSources,
  ],
  (
    searched,
    allPii,
    allLexicon,
    onDetectionsTab,
    filter,
    lexiconUnchecked,
    source,
    bothKinds
  ): Detection[] => {
    if (!onDetectionsTab) {
      return [...allPii, ...allLexicon];
    }
    const enabledTypes = filter
      ? new Set([...filter.direct, ...filter.indirect])
      : null;
    const hiddenLexicons = new Set(lexiconUnchecked ?? []);
    const effectiveSource = bothKinds ? source : 'all';
    return searched.filter((d) => {
      if (isPiiDetection(d)) {
        return (
          effectiveSource !== 'lexicon' &&
          (!enabledTypes || enabledTypes.has(d.piiType))
        );
      }
      if (isLexiconDetection(d)) {
        return (
          effectiveSource !== 'pii' && !hiddenLexicons.has(d.lexicon.lexiconId)
        );
      }
      return true;
    });
  }
);

/** The Detections tab's source strip (VE-30011); `all` with no document. */
export const selectDetectionSource = (
  state: RedactDocumentsState
): DetectionSource => selectSlice(state).session?.detectionSource ?? 'all';

export const selectDetectionsByPage = (
  state: RedactDocumentsState,
  page: number
): Detection[] => {
  const session = selectSlice(state).session;
  if (!session) {
    return [];
  }
  return session.detections.filter((d) =>
    d.occurrences.some((o) => o.page === page)
  );
};

export function selectEffectiveDetectionRect(
  state: RedactDocumentsState,
  detectionId: string,
  page: number,
  rectIdx: number
): BoundingBox | null {
  const session = selectSlice(state).session;
  if (!session) {
    return null;
  }
  const det = session.detections.find((d) => d.id === detectionId);
  if (!det) {
    return null;
  }
  const occ = det.occurrences.find((o) => o.page === page);
  if (!occ || !occ.rects[rectIdx]) {
    return null;
  }
  const override =
    session.detectionRectOverrides[
      detectionRectKey(detectionId, page, rectIdx)
    ];
  return mergeDetectionAndOverride(occ.rects[rectIdx], override);
}

// ---------------------------------------------------------------------------
// Redactions
// ---------------------------------------------------------------------------

export const selectRedactions = (state: RedactDocumentsState): Redaction[] =>
  selectSlice(state).session?.redactions ?? [];

export const selectRedactionsByPage = (
  state: RedactDocumentsState,
  page: number
): Redaction[] => {
  const session = selectSlice(state).session;
  if (!session) {
    return [];
  }
  // Skip inactive entries — they're stored but not rendered or burned
  // in (see `Redaction.redacted` + `buildRedactionPlan`). Including them
  // here would surface unrendered redactions in any future consumer
  // (overlay layer, per-page count, thumbnail badge) and disagree
  // with the rest of the editor.
  return session.redactions.filter(
    (r) => r.page === page && r.redacted !== false
  );
};

export const selectSelectedRedactionIds = (
  state: RedactDocumentsState
): string[] => selectSlice(state).session?.selectedRedactionIds ?? [];

/**
 * The "primary" selected redaction — the first id in the selection set.
 * Single-select consumers (right-click menu anchor, scroll-to behavior) want
 * one Redaction regardless of how many are selected, so this returns the
 * first one and ignores the rest. Multi-select callers should read
 * `selectSelectedRedactionIds` directly.
 */
export const selectSelectedRedaction = (
  state: RedactDocumentsState
): Redaction | null => {
  const session = selectSlice(state).session;
  if (!session || session.selectedRedactionIds.length === 0) {
    return null;
  }
  const id = session.selectedRedactionIds[0];
  return session.redactions.find((r) => r.id === id) ?? null;
};

// ---------------------------------------------------------------------------
// Keyword search
// ---------------------------------------------------------------------------

export const selectKeywordPhrase = (state: RedactDocumentsState): string =>
  selectSlice(state).session?.keywordSearch.phrase ?? '';

export const selectKeywordMatchesByPage = (
  state: RedactDocumentsState,
  page: number
): KeywordMatch[] => {
  const session = selectSlice(state).session;
  if (!session) {
    return [];
  }
  return session.keywordSearch.matches.filter((m) => m.page === page);
};

// ---------------------------------------------------------------------------
// Persistence (auto-save payload)
// ---------------------------------------------------------------------------

export const selectPersistedRedactionPayload = createSelector(
  [selectSession],
  (session) => (session ? buildPersistedRedactionState(session) : null)
);

// ---------------------------------------------------------------------------
// Document comments (VE-21982)
// ---------------------------------------------------------------------------

export const selectDocumentComments = (state: RedactDocumentsState) =>
  selectSlice(state).documentComments;

export const selectIsFetchingDocumentComments = (state: RedactDocumentsState) =>
  selectSlice(state).isFetchingDocumentComments;

export const selectSelectedDocumentComments = (state: RedactDocumentsState) =>
  selectSlice(state).selectedDocumentComments;

export const selectEditingDocumentCommentId = (state: RedactDocumentsState) =>
  selectSlice(state).editingDocumentCommentId;

export const selectDocumentCommentDrafts = (state: RedactDocumentsState) =>
  selectSlice(state).documentCommentDrafts ?? {};

export const selectFocusedDocumentCommentId = (state: RedactDocumentsState) =>
  selectSlice(state).focusedDocumentCommentId;

/**
 * VE-29142 — burn-ins still running that THIS editor started.
 *
 * Reads `burnInsStartedHere` rather than walking the sessions: a burn-in saves
 * before it burns, so its document is clean for most of its run and a clean
 * document switched away from is dropped, not suspended. Walking sessions
 * therefore missed exactly the case the leave guards needed — and left the
 * back button silent about a burn-in the in-app close warned about.
 *
 * `redactFiles` is app-wide and also carries burn-ins from the gallery and the
 * media editor; intersecting with what this editor launched leaves those
 * alone, since leaving this page does not disturb them.
 */
export const selectEditorBurnIns = createSelector(
  (state: RedactDocumentsState) => selectSlice(state)?.burnInsStartedHere,
  (state: RedactDocumentsState) => selectRedactFiles(state as never),
  (startedHere, redactFiles): { tdoId: TDOId; documentName: string }[] =>
    Object.entries(startedHere ?? {})
      .filter(([tdoId]) => redactFiles?.[tdoId]?.status === 'running')
      .map(([tdoId, documentName]) => ({
        tdoId: tdoId as TDOId,
        documentName: String(documentName),
      }))
);

/**
 * VE-29142 — would leaving the editor right now cost the reviewer anything?
 *
 * ONE predicate for every way out, because the three exits kept drifting
 * apart. The in-app close counted unsaved documents and an in-flight Save All;
 * `beforeunload` counted the same two and not a running burn-in; the router
 * had no guard at all, so the BACK BUTTON discarded unsaved redactions in
 * silence. Each exit enumerating its own conditions is exactly how a new kind
 * of at-risk work gets covered in one place and missed in the others.
 *
 * The three costs are genuinely different and the callers word them
 * differently — unsaved work is LOST, a save in flight is ABANDONED, a burn-in
 * is KILLED (the document burn-in is mupdf in this page, not a server job) —
 * so this reports them separately rather than collapsing to a boolean.
 *
 * Memoized, and it must stay that way: it is read from a `useSelector` in the
 * page, and returning fresh arrays on every call re-renders the whole editor
 * tree on every action in the app.
 */
/**
 * The Redact All pass in progress, or null (VE-31006).
 *
 * In the store rather than in the modal because the pass outlives the panel
 * that started it, exactly as a single burn-in outlives the editor.
 */
/**
 * Where the running burn has got to, or null (VE-31309).
 *
 * Carries its own `tdoId` so a caller can check it is showing progress for
 * the file it is drawing rather than for whatever burned last.
 */
export const selectDocumentBurnProgress = (
  state: RedactDocumentsState
): DocumentBurnProgress | null => selectSlice(state)?.burnProgress ?? null;

/** Is the Redact All modal out of the way? Nothing to do with the run. */
export const selectRedactAllMinimised = (
  state: RedactDocumentsState
): boolean => selectSlice(state)?.redactAllMinimised ?? false;

export const selectRedactAllRun = (
  state: RedactDocumentsState
): RedactAllRun | null => selectSlice(state)?.redactAll ?? null;

/**
 * Is a Redact All pass running — burning, or waiting on its combine?
 *
 * Derived in one place because it GATES CONTROLS: the preview's Generate
 * and Update, the panel's merged slot and its footer all have to agree
 * about whether a press is in flight, and they were each writing the
 * comparison out. They did not agree — the slot kept its Generate live
 * through a pass it had started itself, because it was the one still asking
 * only about the combine.
 */
export const selectRedactAllInFlight = (
  state: RedactDocumentsState
): boolean => {
  const run = selectRedactAllRun(state);
  return !!run && run.phase !== 'done';
};

export interface EditorLeaveRisk {
  unsavedDocuments: { tdoId: TDOId; documentName: string }[];
  saveAllInProgress: boolean;
  burnIns: { tdoId: TDOId; documentName: string }[];
  /**
   * A Redact All pass is running (VE-31006).
   *
   * ITS OWN FIELD, because none of the three above can see it. `burnIns`
   * reads `burnInsStartedHere`, which only the EXPORT_REQUEST reducer writes
   * and only for the open session — a pass calls `burnOneDocument` directly,
   * so that list stays empty for the whole run. And once the pass has saved
   * the files that were dirty, `unsavedDocuments` empties too.
   *
   * So all three exits went silent precisely while the most consequential
   * operation in the editor was running, and leaving took the reviewer's
   * remaining unsaved work with it: `CLOSE_DOCUMENT` empties the suspended
   * sessions the pass was going to burn from.
   */
  redactAllRunning: boolean;
}

export const selectEditorLeaveRisk = createSelector(
  selectUnsavedDocuments,
  selectSaveAllInProgress,
  selectEditorBurnIns,
  selectRedactAllRun,
  (
    unsavedDocuments,
    saveAllInProgress,
    burnIns,
    redactAll
  ): EditorLeaveRisk => ({
    unsavedDocuments,
    saveAllInProgress,
    burnIns,
    redactAllRunning: !!redactAll && redactAll.phase !== 'done',
  })
);

/** True when any of the costs applies. */
export const hasEditorLeaveRisk = (risk: EditorLeaveRisk): boolean =>
  risk.unsavedDocuments.length > 0 ||
  risk.saveAllInProgress ||
  risk.burnIns.length > 0 ||
  risk.redactAllRunning;

/** The same question, for callers that only need yes/no from raw state. */
export const selectHasEditorLeaveRisk = (
  state: RedactDocumentsState
): boolean => hasEditorLeaveRisk(selectEditorLeaveRisk(state));

/**
 * What the EDITOR's own open documents say about themselves, by TDO id
 * (VE-31796).
 *
 * The rows every other staleness check reads come from the folder query and
 * are a snapshot. A document the reviewer has been editing in this visit can
 * carry work those rows have never heard of, and it is the document most
 * likely to be behind.
 *
 * AN OVERRIDE IN BOTH DIRECTIONS. `true` adds a file the rows call current;
 * `false` removes one they call stale, because a session burned since that
 * fetch knows better than the fetch.
 *
 * IT IS ABOUT SAVED WORK THE OUTPUT DOES NOT HAVE — `missingLatestEdits`,
 * and only that. UNSAVED work is a different reason with a different remedy
 * (`unsavedChanges`, cleared by saving rather than burning) and
 * `computeRedactedFileStaleness` keeps them apart on purpose. A caller
 * asking "is this member holding the release back" wants BOTH and has to say
 * so: this answers `false` about a file whose edits are only in the browser,
 * and because it overrides in both directions, that answer then REMOVES the
 * file from whatever set it is applied to. `releaseIsBehind` is where that
 * union lives.
 *
 * SUSPENDED SESSIONS COUNT, which is the whole reason this is shared. The
 * editor view used to fold in only the document on screen — and on the
 * Merged PDF tab the open session IS the merged document, so every member
 * was judged from the folder query alone.
 *
 * STABLE WHEN THE VERDICTS ARE. `createSelector` alone would mint a fresh
 * Map on every session identity change, which is every redaction-box
 * mutation, and this map is a dependency of memos that walk every file in
 * the request. The derived booleans almost never flip, so reselect's
 * `resultEqualityCheck` hands back the previous map whenever they match:
 * one shallow comparison in place of four full passes per edit.
 *
 * THROUGH THE LIBRARY, not a module-level variable. The first version kept
 * the previous map in a module global, which works and cannot go stale —
 * the comparison is total — but it is per-MODULE rather than per-selector,
 * so it outlives a store, crosses jest files in one module registry, and
 * makes a reader scanning a selectors file for purity stop and re-derive
 * all of that. `lruMemoize` takes the same comparison as an option.
 */
const sameVerdicts = (
  a: ReadonlyMap<string, boolean>,
  b: ReadonlyMap<string, boolean>
): boolean => {
  if (a.size !== b.size) {
    return false;
  }
  for (const [key, value] of a) {
    if (b.get(key) !== value) {
      return false;
    }
  }
  return true;
};

export const selectSessionStaleness = createSelector(
  [selectSession, selectSuspendedSessions],
  (activeSession, suspendedSessions): ReadonlyMap<string, boolean> => {
    const verdict = new Map<string, boolean>();
    const judge = (session: Parameters<typeof getDocumentStaleness>[0]) => {
      if (!session) {
        return;
      }
      verdict.set(
        String(session.tdoId),
        getDocumentStaleness(session).reasons.includes('missingLatestEdits')
      );
    };
    for (const session of Object.values(suspendedSessions ?? {})) {
      judge(session);
    }
    // The open one LAST, so its live verdict wins over a stale copy of
    // itself still sitting in the suspended map.
    judge(activeSession);
    return verdict;
  },
  { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: sameVerdicts } }
);
